import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, X, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PageHeader } from "@/components/layout/AppShell";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg, fmtDate, fmtEur } from "@/lib/api";

const STATUS = { pending: "text-amber-400 border-amber-500/30", granted: "text-emerald-400 border-emerald-500/30", rejected: "text-red-400 border-red-500/30", active: "text-emerald-400 border-emerald-500/30" };
const LABEL = { pending: "Pendiente", granted: "Concedida", rejected: "Rechazada", active: "Activa", canceled: "Cancelada", refunded: "Reembolsada" };
const Tag = ({ s }) => <span className={`border px-2 py-0.5 text-[11px] uppercase tracking-wider ${STATUS[s] || "border-white/10 text-zinc-400"}`}>{LABEL[s] || s}</span>;
const tabCls = "rounded-none border-b-2 border-transparent px-0 pb-3 text-sm text-zinc-500 data-[state=active]:border-blue-500 data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:shadow-none";

const exportCsv = (rows) => {
  const csv = ["email,origen,fecha", ...rows.map((r) => `${r.email},${r.source},${r.created_at}`)].join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = "suscriptores-hacklabs.csv";
  a.click();
};

export default function AdminCenter() {
  const qc = useQueryClient();
  const { data: reqs = [] } = useQuery({ queryKey: ["admin-reqs"], queryFn: async () => (await api.get("/admin/requests")).data });
  const { data: sales = [] } = useQuery({ queryKey: ["admin-psales"], queryFn: async () => (await api.get("/admin/product-sales")).data });
  const { data: subs = [] } = useQuery({ queryKey: ["admin-subs"], queryFn: async () => (await api.get("/admin/subscribers")).data });
  const act = async (id, action) => {
    try { await api.post(`/admin/requests/${id}/${action}`); toast.success(action === "grant" ? "Acceso concedido con licencia" : "Solicitud rechazada"); qc.invalidateQueries({ queryKey: ["admin-reqs"] }); qc.invalidateQueries({ queryKey: ["admin-psales"] }); } catch (e) { toast.error(errMsg(e)); }
  };
  const pending = reqs.filter((r) => r.status === "pending").length;

  return (
    <div>
      <PageHeader eyebrow="Administración" title="Centro de clientes" subtitle="Solicitudes de contrato, ventas de tu catálogo y audiencia." />
      <div className="px-6 py-10 lg:px-12">
        <Tabs defaultValue="requests">
          <TabsList className="h-auto w-full justify-start gap-6 rounded-none border-b border-white/10 bg-transparent p-0">
            <TabsTrigger value="requests" className={tabCls} data-testid="admin-tab-requests">Solicitudes {pending > 0 && <span className="ml-2 bg-amber-500 px-1.5 text-[10px] font-bold text-black">{pending}</span>}</TabsTrigger>
            <TabsTrigger value="sales" className={tabCls} data-testid="admin-tab-sales">Ventas de productos ({sales.length})</TabsTrigger>
            <TabsTrigger value="audience" className={tabCls} data-testid="admin-tab-audience">Audiencia ({subs.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="requests" className="pt-6">
            {reqs.length === 0 ? <p className="border border-dashed border-white/15 p-10 text-sm text-zinc-500" data-testid="admin-requests-empty">Sin solicitudes todavía.</p> : (
              <div className="border border-white/10">
                {reqs.map((r) => (
                  <div key={r.request_id} className="grid gap-4 border-b border-white/5 p-5 last:border-b-0 lg:grid-cols-[1fr_auto]" data-testid={`admin-request-${r.request_id}`}>
                    <div>
                      <div className="flex flex-wrap items-center gap-3"><p className="font-semibold">{r.product_name}</p><Tag s={r.status} /><span className="text-xs text-zinc-600">{fmtDate(r.created_at)}</span></div>
                      <p className="mt-1 text-sm text-zinc-400">{r.user_name} · <a href={`mailto:${r.user_email}`} className="text-cyan-400">{r.user_email}</a>{r.company && ` · ${r.company}`}{r.phone && ` · ${r.phone}`}</p>
                      <p className="mt-2 whitespace-pre-line text-sm text-zinc-300">{r.message}</p>
                    </div>
                    {r.status === "pending" && (
                      <div className="flex items-start gap-2">
                        <button onClick={() => act(r.request_id, "grant")} className="btn-primary !py-2" data-testid={`admin-request-grant-${r.request_id}`}><Check size={15} /> Conceder</button>
                        <button onClick={() => act(r.request_id, "reject")} className="btn-ghost !py-2" data-testid={`admin-request-reject-${r.request_id}`}><X size={15} /> Rechazar</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
          <TabsContent value="sales" className="pt-6">
            <div className="overflow-x-auto border border-white/10">
              <table className="w-full text-sm" data-testid="admin-product-sales-table">
                <thead><tr className="border-b border-white/10 text-left text-[11px] uppercase tracking-widest text-zinc-500">{["Fecha", "Producto", "Cliente", "Tipo", "Importe", "Licencia", "Estado"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
                <tbody>
                  {sales.map((s) => (
                    <tr key={s.purchase_id} className="border-b border-white/5">
                      <td className="px-4 py-3 text-zinc-400">{fmtDate(s.created_at)}</td><td className="px-4 py-3">{s.product_name}</td>
                      <td className="px-4 py-3 text-zinc-400">{s.buyer_name}<br /><span className="text-xs text-zinc-600">{s.buyer_email}</span></td>
                      <td className="px-4 py-3 text-zinc-400">{{ one_time: "Único", monthly: "Mensual", free: "Gratis", contract: "Contrato" }[s.pricing_type]}</td>
                      <td className="px-4 py-3 font-mono">{fmtEur(s.amount)}</td><td className="px-4 py-3 font-mono text-xs text-cyan-300">{s.license_key}</td><td className="px-4 py-3"><Tag s={s.status} /></td>
                    </tr>
                  ))}
                  {sales.length === 0 && <tr><td colSpan={7} className="p-8 text-zinc-500">Sin ventas todavía.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>
          <TabsContent value="audience" className="space-y-4 pt-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-zinc-400">Personas suscritas a tus lanzamientos. Exporta la lista para tus campañas de email.</p>
              <button onClick={() => exportCsv(subs)} disabled={!subs.length} className="btn-ghost !py-2" data-testid="admin-export-subscribers"><DownloadSimple size={16} /> Exportar CSV</button>
            </div>
            <div className="border border-white/10" data-testid="admin-subscribers-list">
              {subs.map((s) => <div key={s.email} className="flex justify-between border-b border-white/5 px-4 py-3 text-sm last:border-b-0"><span>{s.email}</span><span className="text-xs text-zinc-500">{s.source} · {fmtDate(s.created_at)}</span></div>)}
              {subs.length === 0 && <p className="p-8 text-sm text-zinc-500">Aún no hay suscriptores.</p>}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
